/**
 * Mini-cart loader.
 *
 * Covers the whole cart drawer with a blurred overlay + spinner while an AJAX cart
 * mutation (quantity change, remove, colour swap, …) is in flight, and clears it as
 * soon as the drawer UI re-renders.
 *
 * The overlay lives on `.drawer__inner` (the fixed drawer panel) — NOT inside
 * `cart-drawer-items`, which cart.js replaces on every update — so it survives
 * re-renders. Callers drive it explicitly:
 *     window.MiniCartLoader?.show();   // at the start of a cart AJAX call
 *     window.MiniCartLoader?.hide();   // once the UI has updated (or on error)
 *
 * Robustness:
 *  - Reference-counted: overlapping calls (e.g. rapid quantity clicks) only clear the
 *    overlay once every in-flight call has resolved — no premature hide.
 *  - Self-healing: the panel reference is cached but re-resolved if it leaves the DOM.
 *  - Leak-free: a single shared timer (always cleared), one `{ once: true }` listener,
 *    no per-call listeners, and an idempotent (inject-once) overlay element.
 *  - Safety backstop: a timeout force-clears everything so the loader can never stick.
 */
(() => {
  'use strict';

  const PANEL_SELECTOR = '#CartDrawer .drawer__inner, .drawer__inner';
  const SAFETY_MS = 10000;
  const LOADING_CLASS = 'is-cart-loading';

  let cachedPanel = null;
  let activeCount = 0;
  let safetyTimer = 0;

  /** Resolve the drawer panel, reusing the cached node while it stays connected. */
  const getPanel = () => {
    if (cachedPanel && cachedPanel.isConnected) return cachedPanel;
    cachedPanel = document.querySelector(PANEL_SELECTOR);
    return cachedPanel;
  };

  /** Inject the overlay element once (idempotent). Returns the panel, or null. */
  const ensureOverlay = () => {
    const panel = getPanel();
    if (!panel) return null;
    if (!panel.querySelector(':scope > .mini-cart-loader')) {
      const overlay = document.createElement('div');
      overlay.className = 'mini-cart-loader';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.innerHTML =
        '<span class="mini-cart-loader__spinner" role="status" aria-label="Updating cart"></span>';
      panel.appendChild(overlay);
    }
    return panel;
  };

  /** Force everything off — used by the safety timeout and when the count hits zero. */
  const clear = () => {
    activeCount = 0;
    clearTimeout(safetyTimer);
    safetyTimer = 0;
    const panel = getPanel();
    if (panel) {
      panel.classList.remove(LOADING_CLASS);
      panel.removeAttribute('aria-busy');
    }
  };

  const show = () => {
    const panel = ensureOverlay();
    if (!panel) return;
    activeCount += 1;
    panel.classList.add(LOADING_CLASS);
    panel.setAttribute('aria-busy', 'true');
    clearTimeout(safetyTimer);
    safetyTimer = setTimeout(clear, SAFETY_MS);
  };

  const hide = () => {
    if (activeCount > 0) activeCount -= 1;
    if (activeCount > 0) return; // other calls still in flight
    clear();
  };

  window.MiniCartLoader = { show, hide };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ensureOverlay, { once: true });
  } else {
    ensureOverlay();
  }
})();
